import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ---- Value Conversion Utilities ----
// Normalized [0,1] → threshold dB (-60 to 0 dBFS)
function normToThresholdDb(n) { return (Math.max(0, Math.min(1, n)) - 1) * 60; }
// Normalized [0,1] → compression ratio (1:1 to 20:1)
function normToRatio(n) { return 1 + Math.max(0, Math.min(1, n)) * 19; }
// Normalized [0,1] → attack/hold time (0.1ms to 200ms, log scale)
function normToAttackMs(n) { return 0.1 * Math.pow(2000, Math.max(0, Math.min(1, n))); }
// Normalized [0,1] → release time (1ms to 2000ms, log scale)
function normToReleaseMs(n) { return 1 * Math.pow(2000, Math.max(0, Math.min(1, n))); }
// Normalized [0,1] → gain dB (±20 dB, 0.5 = 0 dB)
function normToGainDb(n) { return (Math.max(0, Math.min(1, n)) - 0.5) * 40; }

function formatMs(ms) {
  if (ms >= 1000) return (ms / 1000).toFixed(2).replace(/\.?0+$/, '') + 's';
  if (ms >= 100) return Math.round(ms) + 'ms';
  if (ms >= 10) return ms.toFixed(1) + 'ms';
  return ms.toFixed(2) + 'ms';
}

function formatRatio(r) {
  if (r >= 19.5) return '∞:1';
  if (r >= 10) return Math.round(r) + ':1';
  return r.toFixed(1) + ':1';
}

// ---- Stereo Compressor Transfer Curve Canvas ----
function StereoCompressorCanvas({ paramsL, paramsR, bypass }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#0d0d0d';
    ctx.fillRect(0, 0, W, H);

    const DB_MIN = -60, DB_MAX = 0;
    const pad = { left: 36, right: 10, top: 10, bottom: 26 };
    const plotW = W - pad.left - pad.right;
    const plotH = H - pad.top - pad.bottom;

    const dbToX = (db) => pad.left + (db - DB_MIN) / (DB_MAX - DB_MIN) * plotW;
    const dbToY = (db) => pad.top + (1 - (db - DB_MIN) / (DB_MAX - DB_MIN)) * plotH;
    const xToDb = (x) => DB_MIN + ((x - pad.left) / plotW) * (DB_MAX - DB_MIN);

    // Grid lines and labels
    const gridDbs = [-60, -48, -36, -24, -12, 0];
    gridDbs.forEach((db) => {
      const x = dbToX(db);
      const y = dbToY(db);

      ctx.strokeStyle = db === 0 ? '#383838' : '#1e1e1e';
      ctx.lineWidth = db === 0 ? 1.5 : 1;

      ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + plotH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + plotW, y); ctx.stroke();

      ctx.fillStyle = db === 0 ? '#666' : '#444';
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(db + '', x, H - 2);

      if (db !== DB_MIN) {
        ctx.textAlign = 'right';
        ctx.fillText(db + '', pad.left - 2, y + 3);
      }
    });

    ctx.fillStyle = '#555';
    ctx.font = '9px monospace';
    ctx.textAlign = 'left';
    ctx.fillText('IN →', pad.left + plotW - 28, H - 2);
    ctx.textAlign = 'right';
    ctx.fillText('↑ OUT', pad.left - 2, pad.top + 8);

    function makeTransferFn(params) {
      const threshDb = normToThresholdDb(params.threshold);
      const ratio = normToRatio(params.ratio);
      const outGainDb = normToGainDb(params.outgain);
      const kneeWidthDb = params.softknee ? 8 : 0;
      return (inDb) => {
        let outDb;
        if (inDb <= threshDb - kneeWidthDb / 2) {
          outDb = inDb;
        } else if (params.softknee && inDb < threshDb + kneeWidthDb / 2) {
          const f = (inDb - (threshDb - kneeWidthDb / 2)) / kneeWidthDb;
          const compressed = threshDb + (inDb - threshDb) / ratio;
          outDb = inDb + f * (compressed - inDb);
        } else {
          outDb = threshDb + (inDb - threshDb) / ratio;
        }
        return Math.min(DB_MAX, outDb + outGainDb);
      };
    }

    // Draw 1:1 reference line
    ctx.beginPath();
    ctx.moveTo(dbToX(DB_MIN), dbToY(DB_MIN));
    ctx.lineTo(dbToX(DB_MAX), dbToY(DB_MAX));
    ctx.strokeStyle = '#2e2e2e';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw L channel (yellow) and R channel (blue) curves
    const channels = [
      { params: paramsL, color: '#f0c040', threshAlpha: 'rgba(240,192,64,0.25)' },
      { params: paramsR, color: '#7ec8e3', threshAlpha: 'rgba(126,200,227,0.25)' },
    ];

    channels.forEach(({ params, color, threshAlpha }) => {
      const threshDb = normToThresholdDb(params.threshold);
      const threshX = dbToX(threshDb);
      if (threshX >= pad.left && threshX <= pad.left + plotW) {
        ctx.beginPath();
        ctx.moveTo(threshX, pad.top);
        ctx.lineTo(threshX, pad.top + plotH);
        ctx.strokeStyle = bypass ? '#2a2a2a' : threshAlpha;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      const transferDb = makeTransferFn(params);
      const pts = [];
      for (let px = 0; px <= plotW; px++) {
        const inDb = xToDb(pad.left + px);
        const outDb = transferDb(inDb);
        pts.push({ x: pad.left + px, y: dbToY(Math.max(DB_MIN, Math.min(DB_MAX, outDb))) });
      }

      ctx.beginPath();
      pts.forEach(({ x, y }, i) => { if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); });
      ctx.strokeStyle = bypass ? '#555' : color;
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }, [paramsL, paramsR, bypass]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={180}
      style={{ width: '100%', height: '180px', display: 'block', borderRadius: '4px', border: '1px solid #2a2a2a' }}
    />
  );
}

// ---- Parameter Row Component ----
function ParamRow({ label, color, value, onChange, displayValue }) {
  return (
    <div className="eq-band">
      <div className="eq-band-label" style={{ color }}>{label}</div>
      <div className="eq-band-param">
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="eq-slider"
        />
        <span className="eq-value">{displayValue}</span>
      </div>
    </div>
  );
}

// ---- localStorage Helpers ----
const MASTER_COMP_LS_KEY = 'uisnapshot_master_comp_presets';
function loadMasterCompPresets() {
  try { const r = localStorage.getItem(MASTER_COMP_LS_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function persistMasterCompPresets(p) {
  try { localStorage.setItem(MASTER_COMP_LS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ---- Default channel parameter values ----
const DEFAULT_PARAMS = {
  threshold: 0.875,
  ratio: 0,
  attack: 0.158,
  hold: 0.158,
  release: 0.650,
  gain: 0.75,
  outgain: 0.333,
  softknee: false,
};

// ---- Main Master Compressor Dialog Component ----
export default function MasterCompressorDialog({ open, onClose, read, write }) {
  const pf = (key, fb) => { const v = parseFloat(read(key)); return isNaN(v) ? fb : v; };

  const [bypass, setBypass] = useState(false);
  const [linked, setLinked] = useState(true);
  const [prname, setPrname] = useState('');

  // L channel state
  const [lThreshold, setLThreshold] = useState(DEFAULT_PARAMS.threshold);
  const [lRatio, setLRatio] = useState(DEFAULT_PARAMS.ratio);
  const [lAttack, setLAttack] = useState(DEFAULT_PARAMS.attack);
  const [lHold, setLHold] = useState(DEFAULT_PARAMS.hold);
  const [lRelease, setLRelease] = useState(DEFAULT_PARAMS.release);
  const [lGain, setLGain] = useState(DEFAULT_PARAMS.gain);
  const [lOutgain, setLOutgain] = useState(DEFAULT_PARAMS.outgain);
  const [lSoftknee, setLSoftknee] = useState(DEFAULT_PARAMS.softknee);

  // R channel state
  const [rThreshold, setRThreshold] = useState(DEFAULT_PARAMS.threshold);
  const [rRatio, setRRatio] = useState(DEFAULT_PARAMS.ratio);
  const [rAttack, setRAttack] = useState(DEFAULT_PARAMS.attack);
  const [rHold, setRHold] = useState(DEFAULT_PARAMS.hold);
  const [rRelease, setRRelease] = useState(DEFAULT_PARAMS.release);
  const [rGain, setRGain] = useState(DEFAULT_PARAMS.gain);
  const [rOutgain, setROutgain] = useState(DEFAULT_PARAMS.outgain);
  const [rSoftknee, setRSoftknee] = useState(DEFAULT_PARAMS.softknee);

  const [presets, setPresets] = useState(loadMasterCompPresets);
  const [selectedPreset, setSelectedPreset] = useState('');

  // Load values from snapshot when dialog opens
  useEffect(() => {
    if (!open) return;
    setBypass(String(read('dyn.bypass')) === '1');
    setLinked(String(read('dyn.linked')) === '1');

    setLThreshold(pf('dyn.l.threshold', DEFAULT_PARAMS.threshold));
    setLRatio(pf('dyn.l.ratio', DEFAULT_PARAMS.ratio));
    setLAttack(pf('dyn.l.attack', DEFAULT_PARAMS.attack));
    setLHold(pf('dyn.l.hold', DEFAULT_PARAMS.hold));
    setLRelease(pf('dyn.l.release', DEFAULT_PARAMS.release));
    setLGain(pf('dyn.l.gain', DEFAULT_PARAMS.gain));
    setLOutgain(pf('dyn.l.outgain', DEFAULT_PARAMS.outgain));
    setLSoftknee(String(read('dyn.l.softknee')) === '1');

    setRThreshold(pf('dyn.r.threshold', DEFAULT_PARAMS.threshold));
    setRRatio(pf('dyn.r.ratio', DEFAULT_PARAMS.ratio));
    setRAttack(pf('dyn.r.attack', DEFAULT_PARAMS.attack));
    setRHold(pf('dyn.r.hold', DEFAULT_PARAMS.hold));
    setRRelease(pf('dyn.r.release', DEFAULT_PARAMS.release));
    setRGain(pf('dyn.r.gain', DEFAULT_PARAMS.gain));
    setROutgain(pf('dyn.r.outgain', DEFAULT_PARAMS.outgain));
    setRSoftknee(String(read('dyn.r.softknee')) === '1');

    const name = read('dyn.prname') || '';
    setPrname(name);
    setSelectedPreset(name);
  }, [open]); // intentionally only re-run when dialog opens/closes

  // Clear preset name when a parameter is changed manually
  const clearPreset = useCallback(() => {
    write('dyn.prname', '');
    write('dyn.prmod', 1);
    setPrname('');
    setSelectedPreset('');
  }, [write]);

  // Write L param (and mirror to R if stereo-linked)
  function handleLChange(key, value, setLFn, setRFn) {
    setLFn(value);
    write(`dyn.l.${key}`, value);
    if (linked) {
      setRFn(value);
      write(`dyn.r.${key}`, value);
    }
    clearPreset();
  }

  // Write R param (and mirror to L if stereo-linked)
  function handleRChange(key, value, setRFn, setLFn) {
    setRFn(value);
    write(`dyn.r.${key}`, value);
    if (linked) {
      setLFn(value);
      write(`dyn.l.${key}`, value);
    }
    clearPreset();
  }

  const paramsL = { bypass, threshold: lThreshold, ratio: lRatio, outgain: lOutgain, softknee: lSoftknee };
  const paramsR = { bypass, threshold: rThreshold, ratio: rRatio, outgain: rOutgain, softknee: rSoftknee };

  // ---- Preset helpers ----
  function buildPresetParams() {
    return {
      'dyn.bypass': bypass ? '1' : '0',
      'dyn.linked': linked ? '1' : '0',
      'dyn.l.threshold': String(lThreshold), 'dyn.l.ratio': String(lRatio),
      'dyn.l.attack': String(lAttack), 'dyn.l.hold': String(lHold),
      'dyn.l.release': String(lRelease), 'dyn.l.gain': String(lGain),
      'dyn.l.outgain': String(lOutgain), 'dyn.l.softknee': lSoftknee ? '1' : '0',
      'dyn.r.threshold': String(rThreshold), 'dyn.r.ratio': String(rRatio),
      'dyn.r.attack': String(rAttack), 'dyn.r.hold': String(rHold),
      'dyn.r.release': String(rRelease), 'dyn.r.gain': String(rGain),
      'dyn.r.outgain': String(rOutgain), 'dyn.r.softknee': rSoftknee ? '1' : '0',
    };
  }

  function applyPresetParams(p, presetName) {
    const parseF = (key, fb) => { const v = parseFloat(p[key]); return isNaN(v) ? fb : v; };

    const newBypass = String(p['dyn.bypass']) === '1';
    setBypass(newBypass); write('dyn.bypass', newBypass ? 1 : 0);

    const newLinked = String(p['dyn.linked']) === '1';
    setLinked(newLinked); write('dyn.linked', newLinked ? 1 : 0);

    const lt = parseF('dyn.l.threshold', DEFAULT_PARAMS.threshold);
    setLThreshold(lt); write('dyn.l.threshold', lt);
    const lr = parseF('dyn.l.ratio', DEFAULT_PARAMS.ratio);
    setLRatio(lr); write('dyn.l.ratio', lr);
    const la = parseF('dyn.l.attack', DEFAULT_PARAMS.attack);
    setLAttack(la); write('dyn.l.attack', la);
    const lh = parseF('dyn.l.hold', DEFAULT_PARAMS.hold);
    setLHold(lh); write('dyn.l.hold', lh);
    const lrel = parseF('dyn.l.release', DEFAULT_PARAMS.release);
    setLRelease(lrel); write('dyn.l.release', lrel);
    const lg = parseF('dyn.l.gain', DEFAULT_PARAMS.gain);
    setLGain(lg); write('dyn.l.gain', lg);
    const lout = parseF('dyn.l.outgain', DEFAULT_PARAMS.outgain);
    setLOutgain(lout); write('dyn.l.outgain', lout);
    const lsk = String(p['dyn.l.softknee']) === '1';
    setLSoftknee(lsk); write('dyn.l.softknee', lsk ? 1 : 0);

    const rt = parseF('dyn.r.threshold', DEFAULT_PARAMS.threshold);
    setRThreshold(rt); write('dyn.r.threshold', rt);
    const rr = parseF('dyn.r.ratio', DEFAULT_PARAMS.ratio);
    setRRatio(rr); write('dyn.r.ratio', rr);
    const ra = parseF('dyn.r.attack', DEFAULT_PARAMS.attack);
    setRAttack(ra); write('dyn.r.attack', ra);
    const rh = parseF('dyn.r.hold', DEFAULT_PARAMS.hold);
    setRHold(rh); write('dyn.r.hold', rh);
    const rrel = parseF('dyn.r.release', DEFAULT_PARAMS.release);
    setRRelease(rrel); write('dyn.r.release', rrel);
    const rg = parseF('dyn.r.gain', DEFAULT_PARAMS.gain);
    setRGain(rg); write('dyn.r.gain', rg);
    const rout = parseF('dyn.r.outgain', DEFAULT_PARAMS.outgain);
    setROutgain(rout); write('dyn.r.outgain', rout);
    const rsk = String(p['dyn.r.softknee']) === '1';
    setRSoftknee(rsk); write('dyn.r.softknee', rsk ? 1 : 0);

    write('dyn.prname', presetName);
    write('dyn.prmod', 0);
    setPrname(presetName);
    setSelectedPreset(presetName);
  }

  function handlePresetSelect(name) {
    setSelectedPreset(name);
    if (!name) return;
    const preset = presets.find((p) => p.name === name);
    if (preset) applyPresetParams(preset.params, name);
  }

  function handleSavePreset() {
    const name = window.prompt('Enter preset name:', prname || '');
    if (!name) return;
    const preset = { name, params: buildPresetParams() };
    const updated = [...presets.filter((p) => p.name !== name), preset];
    setPresets(updated);
    persistMasterCompPresets(updated);
    write('dyn.prname', name);
    write('dyn.prmod', 0);
    setPrname(name);
    setSelectedPreset(name);
  }

  function handleExportPresets() {
    const blob = new Blob([JSON.stringify(presets, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'master-comp-presets.json'; a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportPresets() {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const imported = JSON.parse(ev.target.result);
          if (!Array.isArray(imported)) throw new Error('not array');
          const merged = [...presets];
          let count = 0;
          for (const p of imported) {
            if (p && p.name && p.params) {
              const idx = merged.findIndex((x) => x.name === p.name);
              if (idx >= 0) merged[idx] = p; else merged.push(p);
              count++;
            }
          }
          setPresets(merged); persistMasterCompPresets(merged);
          window.alert('Imported ' + count + ' preset(s).');
        } catch { window.alert('Failed to import: expected a JSON array of preset objects with name and params fields.'); }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  if (!open) return null;

  const COLOR_L = '#f0c040';
  const COLOR_R = '#7ec8e3';

  return ReactDOM.createPortal(
    <div className="eq-overlay" onClick={onClose}>
      <div className="eq-dialog comp-dialog master-comp-dialog" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="eq-header">
          <span className="eq-title">COMP — MASTER (Stereo)</span>
          <div className="eq-header-right">
            <label className="eq-toggle">
              <input
                type="checkbox" checked={bypass}
                onChange={(e) => { const v = e.target.checked; setBypass(v); write('dyn.bypass', v ? 1 : 0); }}
              />
              <span>BYPASS</span>
            </label>
            <label className="eq-toggle">
              <input
                type="checkbox" checked={linked}
                onChange={(e) => { const v = e.target.checked; setLinked(v); write('dyn.linked', v ? 1 : 0); }}
              />
              <span>STEREO LINK</span>
            </label>
            {prname && <span className="eq-active-preset" title={'Preset: ' + prname}>⬡ {prname}</span>}
            <button className="eq-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Stereo transfer curve canvas */}
        <div className="eq-canvas-wrap">
          <StereoCompressorCanvas paramsL={paramsL} paramsR={paramsR} bypass={bypass} />
        </div>

        {/* Channel legend */}
        <div className="geq-legend">
          <span className="geq-legend-item" style={{ color: COLOR_L }}>● L channel</span>
          <span className="geq-legend-item" style={{ color: COLOR_R }}>● R channel</span>
        </div>

        {/* Stereo parameter controls: L and R columns */}
        <div className="master-comp-params">

          {/* L Channel */}
          <div className="master-comp-channel">
            <div className="master-comp-channel-header">
              <span style={{ color: COLOR_L }}>L CHANNEL</span>
              <label className="eq-toggle">
                <input
                  type="checkbox" checked={lSoftknee}
                  onChange={(e) => handleLChange('softknee', e.target.checked ? 1 : 0, setLSoftknee, setRSoftknee)}
                />
                <span>SOFT KNEE</span>
              </label>
            </div>
            <ParamRow
              label="THRESHOLD" color={COLOR_L}
              value={lThreshold}
              onChange={(v) => handleLChange('threshold', v, setLThreshold, setRThreshold)}
              displayValue={normToThresholdDb(lThreshold).toFixed(1) + ' dBFS'}
            />
            <ParamRow
              label="RATIO" color={COLOR_L}
              value={lRatio}
              onChange={(v) => handleLChange('ratio', v, setLRatio, setRRatio)}
              displayValue={formatRatio(normToRatio(lRatio))}
            />
            <ParamRow
              label="ATTACK" color={COLOR_L}
              value={lAttack}
              onChange={(v) => handleLChange('attack', v, setLAttack, setRAttack)}
              displayValue={formatMs(normToAttackMs(lAttack))}
            />
            <ParamRow
              label="HOLD" color={COLOR_L}
              value={lHold}
              onChange={(v) => handleLChange('hold', v, setLHold, setRHold)}
              displayValue={formatMs(normToAttackMs(lHold))}
            />
            <ParamRow
              label="RELEASE" color={COLOR_L}
              value={lRelease}
              onChange={(v) => handleLChange('release', v, setLRelease, setRRelease)}
              displayValue={formatMs(normToReleaseMs(lRelease))}
            />
            <ParamRow
              label="GAIN" color={COLOR_L}
              value={lGain}
              onChange={(v) => handleLChange('gain', v, setLGain, setRGain)}
              displayValue={(normToGainDb(lGain) >= 0 ? '+' : '') + normToGainDb(lGain).toFixed(1) + ' dB'}
            />
            <ParamRow
              label="OUT GAIN" color={COLOR_L}
              value={lOutgain}
              onChange={(v) => handleLChange('outgain', v, setLOutgain, setROutgain)}
              displayValue={(normToGainDb(lOutgain) >= 0 ? '+' : '') + normToGainDb(lOutgain).toFixed(1) + ' dB'}
            />
          </div>

          {/* R Channel */}
          <div className="master-comp-channel">
            <div className="master-comp-channel-header">
              <span style={{ color: COLOR_R }}>R CHANNEL</span>
              <label className="eq-toggle">
                <input
                  type="checkbox" checked={rSoftknee}
                  onChange={(e) => handleRChange('softknee', e.target.checked ? 1 : 0, setRSoftknee, setLSoftknee)}
                />
                <span>SOFT KNEE</span>
              </label>
            </div>
            <ParamRow
              label="THRESHOLD" color={COLOR_R}
              value={rThreshold}
              onChange={(v) => handleRChange('threshold', v, setRThreshold, setLThreshold)}
              displayValue={normToThresholdDb(rThreshold).toFixed(1) + ' dBFS'}
            />
            <ParamRow
              label="RATIO" color={COLOR_R}
              value={rRatio}
              onChange={(v) => handleRChange('ratio', v, setRRatio, setLRatio)}
              displayValue={formatRatio(normToRatio(rRatio))}
            />
            <ParamRow
              label="ATTACK" color={COLOR_R}
              value={rAttack}
              onChange={(v) => handleRChange('attack', v, setRAttack, setLAttack)}
              displayValue={formatMs(normToAttackMs(rAttack))}
            />
            <ParamRow
              label="HOLD" color={COLOR_R}
              value={rHold}
              onChange={(v) => handleRChange('hold', v, setRHold, setLHold)}
              displayValue={formatMs(normToAttackMs(rHold))}
            />
            <ParamRow
              label="RELEASE" color={COLOR_R}
              value={rRelease}
              onChange={(v) => handleRChange('release', v, setRRelease, setLRelease)}
              displayValue={formatMs(normToReleaseMs(rRelease))}
            />
            <ParamRow
              label="GAIN" color={COLOR_R}
              value={rGain}
              onChange={(v) => handleRChange('gain', v, setRGain, setLGain)}
              displayValue={(normToGainDb(rGain) >= 0 ? '+' : '') + normToGainDb(rGain).toFixed(1) + ' dB'}
            />
            <ParamRow
              label="OUT GAIN" color={COLOR_R}
              value={rOutgain}
              onChange={(v) => handleRChange('outgain', v, setROutgain, setLOutgain)}
              displayValue={(normToGainDb(rOutgain) >= 0 ? '+' : '') + normToGainDb(rOutgain).toFixed(1) + ' dB'}
            />
          </div>

        </div>

        {/* Preset bar */}
        <div className="eq-preset-bar">
          <select
            className="eq-preset-select"
            value={selectedPreset}
            onChange={(e) => handlePresetSelect(e.target.value)}
          >
            <option value="">— Select Preset —</option>
            {presets.map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          <button className="eq-btn" onClick={handleSavePreset}>SAVE</button>
          <button className="eq-btn" onClick={handleExportPresets}>EXPORT</button>
          <button className="eq-btn" onClick={handleImportPresets}>IMPORT</button>
        </div>

      </div>
    </div>,
    document.body
  );
}
